/**
 * Renders message text using react-markdown with WhatsApp-style syntax
 * (single * bold, single ~ strikethrough) via preprocess. Supports ** ~~ ` ```, GFM,
 * and standard markdown links: [label](https://example.com). Markdown images are stripped
 * to plain text (alt or URL) so arbitrary image URLs are never loaded.
 *
 * Single newlines are hard breaks (`remark-breaks`), matching RN markdown-it
 * `breaks: true`. A blank line is still a paragraph break.
 *
 * `inline` (e.g. MessagePreview / channel list): unwraps block markdown (headings, tables, …)
 * and skips hard breaks so previews stay one-line friendly. Inline bold/italic/links still apply.
 * `disableLinks` keeps link labels as text (used by the channel list preview).
 *
 * @flow
 */

import * as React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import {
  CHAT_MARKDOWN_DISALLOWED_ELEMENTS,
  CHAT_MARKDOWN_PREVIEW_UNWRAP_ELEMENTS,
} from 'utils/chatMarkdownDisallowedElements';
import remarkStripChatImages from 'utils/remarkStripChatImages';
import { preprocessWhatsAppMarkdown } from 'utils/remarkWhatsAppPreprocess';
import ChatMarkdownLink from './ChatMarkdownLink';

type Props = {
  children: React.Node,
  /** When true, paragraphs render as fragments (for use inside inline elements e.g. preview). */
  inline?: boolean,
  /** Message ID for tracking link clicks (optional). */
  messageId?: string,
  /** Render URL/markdown link labels as text so parent click handlers (e.g. chat list) win. */
  disableLinks?: boolean,
};

function formatWithReactMarkdown(str, index, inline, messageId, disableLinks) {
  const key = `md-${index}`;
  // react-markdown expects a single string; coerce so we never pass array/undefined
  const source =
    typeof str === 'string' ? str : Array.isArray(str) ? str.join('') : String(str ?? '');
  const markdown = String(preprocessWhatsAppMarkdown(source));
  const disallowedElements = inline
    ? [...CHAT_MARKDOWN_DISALLOWED_ELEMENTS, ...CHAT_MARKDOWN_PREVIEW_UNWRAP_ELEMENTS]
    : CHAT_MARKDOWN_DISALLOWED_ELEMENTS;
  return (
    <ReactMarkdown
      key={key}
      remarkPlugins={
        inline
          ? [remarkGfm, remarkStripChatImages]
          : [remarkGfm, remarkBreaks, remarkStripChatImages]
      }
      disallowedElements={disallowedElements}
      unwrapDisallowed={inline}
      // eslint-disable-next-line @eslint-react/jsx-no-children-prop -- react-markdown API uses children for source string
      children={markdown}
      components={{
        p: ({ children: paragraphChildren }) =>
          inline ? <>{paragraphChildren}</> : <p>{paragraphChildren}</p>,
        a: ({ href, children: anchorChildren }) =>
          disableLinks ? (
            <>{anchorChildren}</>
          ) : (
            <ChatMarkdownLink href={href} messageId={messageId}>
              {anchorChildren}
            </ChatMarkdownLink>
          ),
      }}
    />
  );
}

const WhatsAppTextFormatter = ({
  children,
  inline = false,
  messageId,
  disableLinks = false,
}: Props) => {
  return React.Children.map(children, (child, index) => {
    if (typeof child === 'string') {
      return formatWithReactMarkdown(child, index, inline, messageId, disableLinks);
    }
    return child;
  });
};

export default WhatsAppTextFormatter;
